import { useEffect } from 'react';
import { throttle } from 'lodash';
import { FIGMA_EMBED_MESSAGE_TYPES } from '@/consts/figma-embed-messages';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';

/**
 * Reports in-iframe user activity to the Figma parent so its edit-lock idle
 * timer stays accurate. The parent only sees interaction with Figma's own chrome
 * (the Weave canvas is a cross-origin iframe), so without this an editor working
 * entirely inside Weave for >10 min would be treated as idle and lose the lock
 * mid-edit. We mirror the parent's activity event set (pointer/key/wheel) and
 * post a throttled, payload-free `WEAVE_USER_ACTIVITY` — the parent only needs a
 * coarse "still here" pulse to refresh `lastActivityRef`.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;
const ACTIVITY_THROTTLE_MS = 5000;

export function FigmaEmbedActivityReporter(): null {
  const isFigmaEmbedMode = useIsFigmaEmbedMode();

  useEffect(() => {
    if (!isFigmaEmbedMode) return;
    if (window.parent === window) return;

    const report = throttle(
      () => {
        try {
          window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.USER_ACTIVITY }, '*');
        } catch {
          // postMessage can throw if the parent went away; activity reporting is
          // best-effort, so swallow.
        }
      },
      ACTIVITY_THROTTLE_MS,
      { leading: true, trailing: false },
    );

    // Capture phase so a handler that calls stopPropagation can't hide activity.
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, report, { passive: true, capture: true }));
    return () => {
      report.cancel();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, report, true));
    };
  }, [isFigmaEmbedMode]);

  return null;
}
